/**
 * @license
 * @copyright Copyright Motili Inc., 2021 All Rights Reserved
 */

import { ChangeEvent, ReactElement } from 'react';
import { styled } from 'styled-components';
import { Heading1, Paragraph, RadioGroup, Checkbox } from '@motili/ui';
import CenteredContainer from '../../../components/layout/CenteredContainer';
import StyledTextInput from '../../../components/styled/StyledTextInput';
import { useAppDispatch, useAppSelector } from '../../../common/redux/hooks';
import AddressForm from '../../../components/AddressForm';
import BottomButtonNavigation from '../../../navigation/BottomButtonNavigation';
import { Address } from '../../../common/types';
import UseCompanyAsBillingCheckbox from '../../../components/UseCompanyAsBillingCheckbox';
import { setCompanyInfo } from '../../../common/redux/ducks/clientRegistrationForm';

interface YourCompanyProps {
    handleBackClick: () => void;
    handleNextClick: () => void;
}

export default function YourCompany({
    handleBackClick,
    handleNextClick,
}: YourCompanyProps): ReactElement {
    const dispatch = useAppDispatch();
    const companyInfo = useAppSelector(
        ({ clientRegistration }) => clientRegistration.client.companyInfo
    );
    const steps = useAppSelector(
        state => state['clientRegistration'].client.totalSteps
    );

    const handleInputChange = ({
        target: { name, value },
    }: ChangeEvent<HTMLInputElement>) => {
        dispatch(
            setCompanyInfo({
                ...companyInfo,
                [name]: value,
            })
        );
    };

    const companyAddressKeys = Object.keys(companyInfo.companyAddress);
    const billingAddressKeys = Object.keys(companyInfo.billingAddress);

    const missingCompanyAddressFields =
        companyAddressKeys
            .filter(key => key !== 'address2')
            .filter(key => companyInfo.companyAddress[key as keyof Address])
            .length < 4;

    const missingBillingAddressFields =
        billingAddressKeys
            .filter(key => key !== 'address2')
            .filter(key => companyInfo.billingAddress[key as keyof Address])
            .length < 4;

    const checkZip = () => {
        return (
            !/(^\d{5}$)/.test(companyInfo.companyAddress.zip) ||
            !/(^\d{5}$)/.test(companyInfo.billingAddress.zip)
        );
    };

    const handleDoingBusinessAsCheckboxChange = (e: any) => {
        dispatch(
            setCompanyInfo({
                ...companyInfo,
                useNameAsDBA: e.target.checked,
            })
        );
    };

    const handleRadioInputChange = (e: any) => {
        dispatch(
            setCompanyInfo({
                ...companyInfo,
                headquarteredInCalifornia: e.target.value === 'true',
            })
        );
    };

    const handleCompanyInfoOnChange = (updates: any) => {
        dispatch(
            setCompanyInfo({
                ...companyInfo,
                ...updates,
            })
        );
    };

    return (
        <CenteredContainer>
            <Paragraph>
                <BoldText>Step 2</BoldText> of {steps}
            </Paragraph>
            <Heading1>Your Company</Heading1>
            <Paragraph>
                The Information will be used in a way to group your own network
                of contractors. Even if you don’t have a larger network, we
                still like to have this information on hand to keep our system
                strong.
            </Paragraph>
            <StyledTextInput
                marginTop='20px'
                required
                name={'name'}
                errorMessage={'Name cannot be blank'}
                onChange={handleInputChange}
                value={companyInfo.name}
                style={inputStyles}
                label='Company Name'
            />
            <Checkbox
                checked={companyInfo.useNameAsDBA || false}
                label={'Doing Business As (DBA) is same as above'}
                onChange={handleDoingBusinessAsCheckboxChange}
                name={'doingBusinessAs'}
                value={'false'}
                id={'doingBusinessAs'}
            />
            {!companyInfo.useNameAsDBA && (
                <StyledTextInput
                    marginTop='20px'
                    required
                    name={'doingBusinessAs'}
                    errorMessage={'DBA cannot be blank'}
                    onChange={handleInputChange}
                    value={companyInfo?.doingBusinessAs || ''}
                    style={inputStyles}
                    label='Doing Business As'
                />
            )}
            <AddressForm
                addressType={'companyAddress'}
                companyInfo={companyInfo}
                handleCompanyInfoOnChange={handleCompanyInfoOnChange}
            />
            <UseCompanyAsBillingCheckbox
                companyInfo={companyInfo}
                handleCompanyInfoOnChange={handleCompanyInfoOnChange}
            />
            {!companyInfo.useCompanyAddressAsBilling && (
                <AddressForm
                    addressType='billingAddress'
                    companyInfo={companyInfo}
                    handleCompanyInfoOnChange={handleCompanyInfoOnChange}
                />
            )}
            <br />
            <RadioGroup
                className=''
                key={'headquartered-in-cali'}
                onChange={handleRadioInputChange}
                name={'headquartered-in-cali'}
                options={headquarteredInCaliforniaOptions.map(opt => ({
                    ...opt,
                    checked: companyInfo.headquarteredInCalifornia || false,
                }))}
                required
                label={'Is your company headquartered in California?'}
            />
            <BottomButtonNavigation
                onBackClick={handleBackClick}
                onNextClick={handleNextClick}
                nextDisabled={
                    !companyInfo.name ||
                    (!companyInfo.useNameAsDBA &&
                        !companyInfo.doingBusinessAs) ||
                    checkZip() ||
                    missingCompanyAddressFields ||
                    missingBillingAddressFields
                }
            />
        </CenteredContainer>
    );
}

const headquarteredInCaliforniaOptions = [
    { value: `true`, label: 'Yes, we are headquartered in California' },
    {
        value: `false`,
        label: 'No, we are NOT headquartered in California',
    },
];

const BoldText = styled.span`
    font-weight: bold;
`;

const inputStyles = { width: '100%', margin: '10px 0' };
